import { useState, useEffect } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Sun, Moon, ShoppingCart, Sparkles, Menu, X, Search, MessageCircle } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useMotion } from '../context/MotionContext';
import CursorGlow from './CursorGlow';
import SearchOverlay from './SearchOverlay.jsx';
import { trackPageView } from '../lib/track';
import LogoutButton from './LogoutButton';
import CartWaitingBanner from './CartWaitingBanner';
import StorePausedPage from './StorePausedPage';
import { useStoreStatus } from '../context/StoreStatusContext';

// Minimal menu (Chase 2026-07-22): the 3D OptionWheel radial menu is gone,
// replaced by a plain centered list of links. Same destinations, none of the
// spin. The header still floats over the page (no bar, no border).
// While the store is paused, visitors get the pause page everywhere except the
// places that are not shopping: signing in, an existing order, unsubscribing.
const PAUSE_OPEN_PATHS = ['/login', '/reset-password', '/order', '/unsubscribe', '/account'];
const pathAllowedWhilePaused = (p) => PAUSE_OPEN_PATHS.some((o) => p === o || p.startsWith(`${o}/`));

const MENU_LINKS = [
  { label: 'Home', to: '/' },
  { label: 'Shop', to: '/shop' },
  { label: 'Track Order', to: '/order/lookup' },
  { label: 'Cart', to: '/cart' },
];

export default function Layout() {
  const { theme, toggleTheme } = useTheme();
  const { itemCount } = useCart();
  const { user, isAdmin } = useAuth();
  const { paused } = useStoreStatus();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { motionOn, motionPref, toggleMotion } = useMotion();
  // The nav floats over the page. On the homepage it sits on the dark vault
  // photo, so text/icons go white there; ink everywhere else.
  const onDark = pathname === '/';
  const headerInk = onDark ? '#f4f2ff' : 'var(--text)';
  const headerBorder = onDark ? 'rgba(244,242,255,0.35)' : 'var(--border)';
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Every route change is a page view. Deduped inside track() so a re-render
  // is not counted as a second visit.
  useEffect(() => { trackPageView(pathname); }, [pathname]);

  const go = (to) => {
    setMenuOpen(false);
    navigate(to);
  };

  const goAccount = () => {
    setMenuOpen(false);
    navigate(user ? (isAdmin ? '/admin/orders' : '/account/orders') : '/login');
  };

  // Label reflects who's signed in: staff -> "Staff Area", member -> "My
  // Account", logged out -> "Account" (routes to login).
  const accountLabel = user ? (isAdmin ? 'Staff Area' : 'My Account') : 'Account';

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <CursorGlow enabled={motionOn} />
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
      <header
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 20,
          color: headerInk,
        }}
      >
        <div
          className="sv-container"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 76 }}
        >
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 900, fontSize: '1.15rem', letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>
            <Sparkles size={20} color={onDark ? '#c3b5ff' : 'var(--accent)'} />
            Shiny Vault LGS
          </Link>

          <nav className="sv-header-links" style={{ display: 'flex', alignItems: 'center', gap: 22, fontSize: '0.92rem', fontWeight: 600 }}>
            <Link to="/shop">Shop</Link>
            <Link to="/order/lookup">Track order</Link>
          </nav>

          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <button
              onClick={() => setSearchOpen(true)}
              aria-label="Search products"
              style={{ display: 'flex', background: 'transparent', border: 'none', color: 'inherit', padding: 0, cursor: 'pointer' }}
            >
              <Search size={22} />
            </button>
            <Link to="/cart" style={{ position: 'relative', display: 'flex' }} aria-label="Cart">
              <ShoppingCart size={22} />
              {itemCount > 0 && (
                <span
                  style={{
                    position: 'absolute', top: -6, right: -8, background: 'var(--accent)', color: '#ffffff',
                    borderRadius: 999, fontSize: '0.68rem', fontWeight: 800, padding: '1px 6px', lineHeight: '14px',
                  }}
                >
                  {itemCount}
                </span>
              )}
            </Link>
            <button
              onClick={() => setMenuOpen(true)}
              style={{ padding: 8, borderRadius: 999, display: 'flex', background: 'transparent', border: `1px solid ${headerBorder}`, color: 'inherit' }}
              aria-label="Open menu"
            >
              <Menu size={18} />
            </button>
          </div>
        </div>
      </header>

      <main
        className={onDark ? undefined : 'sv-main'}
        style={{ flex: 1, position: 'relative', zIndex: 1 }}
      >
        {(() => {
          // Staff keep the full store while it is paused, with a reminder that
          // the public cannot see it. Everyone else gets the pause page.
          const blocked = paused && !isAdmin && !pathAllowedWhilePaused(pathname);
          if (blocked) return <StorePausedPage />;
          return (
            <>
              {paused && isAdmin && (
                <div
                  style={{
                    position: 'relative', zIndex: 2, padding: '8px 16px', textAlign: 'center',
                    fontSize: '0.8rem', fontWeight: 700,
                    background: 'var(--warning)', color: '#1a1a1a',
                  }}
                >
                  Store is paused. Visitors see the inventory page. Only staff see this view.{' '}
                  <Link to="/admin/orders" style={{ color: 'inherit', textDecoration: 'underline' }}>Reopen in admin</Link>
                </div>
              )}
              <CartWaitingBanner />
              <Outlet />
            </>
          );
        })()}
      </main>

      <footer className="sv-footer" style={{ borderTop: '1px solid var(--border)', position: 'relative', zIndex: 1 }}>
        <div className="sv-container" style={{
          fontSize: '0.8rem', color: 'var(--text-muted)', display: 'flex',
          gap: 16, flexWrap: 'wrap', alignItems: 'center',
        }}>
          <span>Shiny Vault LGS</span>
          <Link to="/shipping" style={{ color: 'var(--text-muted)' }}>Shipping and pricing</Link>
          <Link to="/order/lookup" style={{ color: 'var(--text-muted)' }}>Track order</Link>
          <a
            href="https://instagram.com/trainercenter.pokemon"
            target="_blank"
            rel="noreferrer"
            style={{ color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <MessageCircle size={14} />
            Having trouble? DM us @trainercenter.pokemon
          </a>
        </div>
      </footer>

      {menuOpen && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 90,
            background: theme === 'dark' ? 'rgba(11,11,18,0.96)' : 'rgba(246,244,239,0.96)',
            backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
            display: 'flex', flexDirection: 'column',
          }}
        >
          <div className="sv-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', height: 76 }}>
            <button
              onClick={() => setMenuOpen(false)}
              style={{ padding: 8, borderRadius: 999, display: 'flex', background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)' }}
              aria-label="Close menu"
            >
              <X size={18} />
            </button>
          </div>

          <nav
            style={{
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', gap: 6,
            }}
          >
            {MENU_LINKS.map((item) => (
              <button
                key={item.to}
                onClick={() => go(item.to)}
                style={{
                  background: 'none', border: 'none', color: 'var(--text)',
                  fontSize: 'clamp(1.8rem, 7vw, 2.6rem)', fontWeight: 800, letterSpacing: '-0.01em',
                  padding: '8px 16px', lineHeight: 1.2,
                }}
              >
                {item.label}
              </button>
            ))}
            <button
              onClick={goAccount}
              style={{
                background: 'none', border: 'none', color: 'var(--text)',
                fontSize: 'clamp(1.8rem, 7vw, 2.6rem)', fontWeight: 800, letterSpacing: '-0.01em',
                padding: '8px 16px', lineHeight: 1.2,
              }}
            >
              {accountLabel}
            </button>
            {user && (
              <LogoutButton
                onBeforeLogout={() => setMenuOpen(false)}
                iconSize={22}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 12,
                  background: 'none', border: 'none', color: 'var(--text-muted)',
                  fontSize: 'clamp(1.1rem, 4vw, 1.5rem)', fontWeight: 700, letterSpacing: '-0.01em',
                  padding: '8px 16px', lineHeight: 1.2, marginTop: 8,
                }}
              />
            )}
          </nav>

          {/* Contact us. The store has no phone line and no inbox of its own,
              so Instagram DMs are the support channel. Saying what people
              actually write in about beats a bare "Contact" link. */}
          <div className="sv-container" style={{ paddingBottom: 6 }}>
            <a
              href="https://instagram.com/trainercenter.pokemon"
              target="_blank"
              rel="noreferrer"
              onClick={() => setMenuOpen(false)}
              style={{
                display: 'block', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
                padding: '16px 18px', color: 'var(--text)', textAlign: 'center',
              }}
            >
              <span
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  fontWeight: 800, fontSize: '1.05rem',
                }}
              >
                <MessageCircle size={18} />
                Contact us
              </span>
              <span
                style={{
                  display: 'block', marginTop: 6, fontSize: '0.84rem',
                  color: 'var(--text-muted)', lineHeight: 1.5,
                }}
              >
                Trouble with the site, a question about a price, help with an
                order, or anything else. Send us a DM at @trainercenter.pokemon.
              </span>
            </a>
          </div>

          <div
            className="sv-container"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: 34, paddingTop: 18 }}
          >
            <button
              onClick={toggleTheme}
              style={{
                borderRadius: 999, padding: '11px 20px', display: 'flex', alignItems: 'center', gap: 8,
                background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)',
                fontWeight: 600, fontSize: '0.85rem',
              }}
            >
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
              {theme === 'dark' ? 'Light mode' : 'Dark mode'}
            </button>
            <button
              onClick={toggleMotion}
              style={{
                borderRadius: 999, padding: '11px 20px', display: 'flex', alignItems: 'center', gap: 8,
                background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)',
                fontWeight: 600, fontSize: '0.85rem', opacity: motionPref === 'on' ? 1 : 0.55,
              }}
            >
              {motionPref === 'on' ? 'Motion: on' : 'Motion: off'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
